import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { isAnnotationSeries } from 'src/app/model/annotation-series';
import { isDeviceSeries } from 'src/app/model/device/device-series';
import { IGraphConfig } from 'src/app/model/graph-configs/graph-config';
import { ITab } from 'src/app/model/tab';
import { isThingieSeries } from 'src/app/model/thingie-series';
import { IThingie } from 'src/app/model/thingie/thingie';
import { isScatterSeries } from 'src/app/model/values-config';
import { ActionProposal, ChatHiddenContext, DuplicateExperimentWizardActionProposal, ExperimentComparisonContext, ExperimentSummaryContext, GraphAgentContext, GraphAgentSeriesSummary } from 'src/app/services/chat/chat.service';
import { getThingieGroupId, getThingieReplicateIds } from 'src/app/utility/thingie-group-helper';
import { ThingieHelper } from 'src/app/utility/thingie-helper';
import { ThingieStatusHelper } from 'src/app/utility/thingie-status-helper';
import { DeviceService } from '../device/device.service';
import { ThingieService } from '../thingie/thingie-service.service';
import { ExperimentAnnotationsContextService } from './experiment-annotations-context.service';
import { buildExperimentSampleBundleKey, ExperimentSampleDatasetService } from './experiment-sample-dataset.service';

type DeviceInputLike = {
  source?: string;
  deviceAddress?: string;
};

@Injectable({ providedIn: 'root' })
export class GraphAgentContextService {
  private static readonly MAX_CONTEXT_BYTES = 32_000;

  constructor(
    private deviceService: DeviceService,
    private thingieService: ThingieService,
    private experimentSampleDatasetService: ExperimentSampleDatasetService,
    private experimentAnnotationsContextService: ExperimentAnnotationsContextService
  ) {}

  public async buildHiddenContextForThingie(
    thingie: IThingie,
    tabs: ITab[] = [],
    options?: { linkedThingieIds?: string[] }
  ): Promise<ChatHiddenContext> {
    return {
      graphAgentContext: await this.buildGraphAgentContext(thingie, tabs, options?.linkedThingieIds)
    };
  }

  public async buildHiddenContextForGraphConfig(graphConfig: IGraphConfig): Promise<ChatHiddenContext | undefined> {
    const thingieId = this.getPrimaryThingieId(graphConfig);
    if (!thingieId) {
      return undefined;
    }

    const thingie = await firstValueFrom(this.thingieService.getThingieById(thingieId));
    return {
      graphAgentContext: await this.buildGraphAgentContext(thingie, [{
        backendId: null,
        graphConfig,
        outputData: [],
        legend: [],
        _isDashboardCard: false,
        _extraData: {}
      }], this.collectLinkedThingieIds(graphConfig))
    };
  }

  public async enrichHiddenContextForMessage(
    message: string,
    hiddenContext?: ChatHiddenContext
  ): Promise<ChatHiddenContext | undefined> {
    if (!hiddenContext?.graphAgentContext) {
      return hiddenContext;
    }

    const hiddenContextWithSamples = await this.ensureExperimentSampleData(hiddenContext);
    const hiddenContextWithAnnotations = await this.refreshExperimentAnnotations(hiddenContextWithSamples);
    const graphAgentContext = hiddenContextWithAnnotations.graphAgentContext;
    if (!graphAgentContext) {
      return hiddenContextWithAnnotations;
    }

    const experimentComparison = await this.maybeBuildExperimentComparison(
      message,
      graphAgentContext
    );
    if (!experimentComparison) {
      return hiddenContextWithAnnotations;
    }

    return {
      ...hiddenContextWithAnnotations,
      graphAgentContext: {
        ...graphAgentContext,
        experimentComparison
      }
    };
  }

  public getImmediateActionProposals(message: string, hiddenContext?: ChatHiddenContext): ActionProposal[] {
    const duplicateAction = this.maybeBuildDuplicateWizardAction(message, hiddenContext?.graphAgentContext);
    return duplicateAction ? [duplicateAction] : [];
  }

  public async getSelectorVisibleSeriesForThingie(thingie: IThingie) {
    const measurementSeries = this.thingieService.getAvailableDataSeriesFromThingie(thingie, false);
    const annotationSeries = this.thingieService.getAnnotationSeriesFromThingie(thingie);
    const deviceSeries = await this.getDeviceSeriesForThingie(thingie);
    return [...measurementSeries, ...deviceSeries, ...annotationSeries];
  }

  private async buildGraphAgentContext(
    thingie: IThingie,
    tabs: ITab[],
    linkedThingieIds?: string[]
  ): Promise<GraphAgentContext> {
    const visibleSeries = await this.getSelectorVisibleSeriesForThingie(thingie);
    const seriesSummaries = visibleSeries.map(series => this.toSeriesSummary(series, thingie));
    const currentTabs = tabs
      .filter(tab => tab._isDashboardCard !== true)
      .map(tab => ({
        uuid: tab.graphConfig.uuid,
        label: tab.graphConfig.label,
        seriesCount: tab.graphConfig.internalSeriesList.length,
        timeReference: tab.graphConfig.timeReference
      }));
    const experimentSummary = await this.buildExperimentSummary(thingie);
    const experimentSampleData = await this.experimentSampleDatasetService.getOrBuildForThingie(thingie);
    const experimentAnnotations = await this.buildExperimentAnnotations(thingie, linkedThingieIds);

    const baseContext: GraphAgentContext = {
      thingieId: thingie._id ?? '',
      thingieName: ThingieHelper.getReplicateFullname(thingie),
      experimentName: thingie.baseName,
      availableSeries: [],
      currentTabs,
      templateName: thingie.createdFromTemplateId,
      experimentSummary,
      experimentSampleData,
      experimentAnnotations
    };

    const availableSeries = this.truncateSeriesSummaries(baseContext, seriesSummaries);

    return {
      ...baseContext,
      availableSeries,
      truncated: availableSeries.length < seriesSummaries.length ? true : undefined
    };
  }

  private async ensureExperimentSampleData(hiddenContext: ChatHiddenContext): Promise<ChatHiddenContext> {
    const graphAgentContext = hiddenContext.graphAgentContext;
    if (!graphAgentContext) {
      return hiddenContext;
    }

    if (graphAgentContext.experimentSampleData?.manifest && graphAgentContext.experimentSampleData.payloads?.length) {
      return hiddenContext;
    }

    const thingie = await firstValueFrom(this.thingieService.getThingieById(graphAgentContext.thingieId));
    const experimentSampleData = await this.experimentSampleDatasetService.getOrBuildForThingie(thingie);
    if (!experimentSampleData) {
      return hiddenContext;
    }

    return {
      ...hiddenContext,
      graphAgentContext: {
        ...graphAgentContext,
        experimentSampleData
      }
    };
  }

  private async refreshExperimentAnnotations(hiddenContext: ChatHiddenContext): Promise<ChatHiddenContext> {
    const graphAgentContext = hiddenContext.graphAgentContext;
    if (!graphAgentContext?.thingieId) {
      return hiddenContext;
    }

    const thingie = await firstValueFrom(this.thingieService.getThingieById(graphAgentContext.thingieId));
    const experimentAnnotations = await this.buildExperimentAnnotations(
      thingie,
      graphAgentContext.experimentAnnotations?.manifest.sourceThingieIds
    );

    return {
      ...hiddenContext,
      graphAgentContext: {
        ...graphAgentContext,
        experimentAnnotations
      }
    };
  }

  private async buildExperimentAnnotations(
    thingie: IThingie,
    linkedThingieIds?: string[]
  ) {
    const sourceThingieIds = this.normalizeThingieIds(linkedThingieIds ?? [thingie._id ?? '']);
    const annotationThingies = await this.resolveAnnotationThingies(thingie, sourceThingieIds);
    if (annotationThingies.length === 0) {
      return undefined;
    }

    return this.experimentAnnotationsContextService.buildFromThingies(annotationThingies, {
      experimentName: thingie.baseName,
      experimentGroupId: getThingieGroupId(sourceThingieIds.length > 0
        ? sourceThingieIds
        : [thingie._id ?? ''])
    });
  }

  private async resolveAnnotationThingies(
    primaryThingie: IThingie,
    linkedThingieIds: string[]
  ): Promise<IThingie[]> {
    if (linkedThingieIds.length === 0) {
      return primaryThingie._id ? [primaryThingie] : [];
    }

    if (linkedThingieIds.length === 1 && linkedThingieIds[0] === primaryThingie._id) {
      return [primaryThingie];
    }

    const fetchedThingies = await firstValueFrom(this.thingieService.getThingiesByIds(linkedThingieIds));
    return fetchedThingies.length > 0 ? fetchedThingies : [primaryThingie];
  }

  private normalizeThingieIds(thingieIds: string[]): string[] {
    return [...new Set(thingieIds.map(thingieId => thingieId.trim()).filter(Boolean))].sort();
  }

  private collectLinkedThingieIds(graphConfig: IGraphConfig): string[] {
    const linkedThingieIds = new Set<string>();

    if (graphConfig.thingie) {
      linkedThingieIds.add(graphConfig.thingie);
    }

    graphConfig.internalSeriesList.forEach(series => {
      const thingieId = series.identification.thingieId;
      if (thingieId) {
        linkedThingieIds.add(thingieId);
      }
    });

    return [...linkedThingieIds].sort();
  }

  private async buildExperimentSummary(thingie: IThingie): Promise<ExperimentSummaryContext> {
    const replicateIds = getThingieReplicateIds(thingie);
    const replicateThingies = replicateIds.length > 1
      ? await firstValueFrom(this.thingieService.getThingiesByIds(replicateIds))
      : [thingie];

    return {
      replicateCount: replicateThingies.length,
      replicateNames: replicateThingies
        .map(replicate => ThingieHelper.getReplicateFullname(replicate))
        .sort((a, b) => a.localeCompare(b)),
      templateName: thingie.createdFromTemplateId,
      processCount: thingie.processes.filter(process => process.removed !== true).length,
      taskSummaries: thingie.processes.flatMap((process, processIndex) =>
        process.tasks
          .filter(task => task.removed !== true)
          .map((task, taskIndex) => ({
            processIndex,
            taskIndex,
            taskLabel: task.name,
            deviceType: this.getTaskDeviceType(task)
          }))),
      state: ThingieStatusHelper.getThingieStatus(thingie).status,
      created: thingie.created
    };
  }

  private async maybeBuildExperimentComparison(
    message: string,
    currentContext: GraphAgentContext
  ): Promise<ExperimentComparisonContext | undefined> {
    const targetExperimentName = this.extractComparisonTargetName(message, currentContext.experimentName);
    if (!targetExperimentName) {
      return undefined;
    }

    const allThingies = await firstValueFrom(this.thingieService.getThingieList());
    const normalizedTarget = targetExperimentName.toLowerCase();
    const targetThingie = allThingies.find(
      thingie => thingie.baseName.toLowerCase() === normalizedTarget
        && thingie._id !== currentContext.thingieId
    );

    if (!targetThingie) {
      return undefined;
    }

    const targetSummary = await this.buildExperimentSummary(targetThingie);
    const currentSummary = currentContext.experimentSummary ?? {
      replicateCount: 0,
      replicateNames: [],
      processCount: 0,
      taskSummaries: []
    };

    return {
      targetExperimentName: targetThingie.baseName,
      current: currentSummary,
      target: targetSummary,
      differences: this.computeExperimentDifferences(currentSummary, targetSummary)
    };
  }

  private maybeBuildDuplicateWizardAction(
    message: string,
    currentContext?: GraphAgentContext
  ): DuplicateExperimentWizardActionProposal | undefined {
    if (!currentContext?.thingieId) {
      return undefined;
    }

    const normalized = message.toLowerCase();
    const referencesDuplicate = normalized.includes('duplicate') || normalized.includes('copy this experiment');
    const referencesExperiment = normalized.includes('experiment') || normalized.includes('replicate');
    if (!referencesDuplicate || !referencesExperiment) {
      return undefined;
    }

    return {
      action: 'duplicateAndOpenWizard',
      explanation: `Open the duplication wizard for ${currentContext.experimentName}.`,
      referenceThingieId: currentContext.thingieId
    };
  }

  private computeExperimentDifferences(current: ExperimentSummaryContext, target: ExperimentSummaryContext): string[] {
    const differences: string[] = [];

    if ((current.templateName ?? '') !== (target.templateName ?? '')) {
      differences.push(`Template differs: ${current.templateName ?? 'unknown'} vs ${target.templateName ?? 'unknown'}.`);
    }
    if (current.replicateCount !== target.replicateCount) {
      differences.push(`Replicate count differs: ${current.replicateCount} vs ${target.replicateCount}.`);
    }
    if (current.processCount !== target.processCount) {
      differences.push(`Process count differs: ${current.processCount} vs ${target.processCount}.`);
    }

    const currentTasks = new Set(current.taskSummaries.map(task => task.taskLabel));
    const targetTasks = new Set(target.taskSummaries.map(task => task.taskLabel));
    const onlyInCurrent = [...currentTasks].filter(task => !targetTasks.has(task));
    const onlyInTarget = [...targetTasks].filter(task => !currentTasks.has(task));

    if (onlyInCurrent.length > 0) {
      differences.push(`Only in current experiment: ${onlyInCurrent.join(', ')}.`);
    }
    if (onlyInTarget.length > 0) {
      differences.push(`Only in target experiment: ${onlyInTarget.join(', ')}.`);
    }

    if (differences.length === 0) {
      differences.push('No major structural differences detected in the summarized experiment configuration.');
    }

    return differences;
  }

  private extractComparisonTargetName(message: string, currentExperimentName: string): string | undefined {
    const quotedMatch = message.match(/compare(?:\s+this\s+experiment)?\s+(?:against|to|with)\s+"([^"]+)"/i);
    if (quotedMatch?.[1]) {
      const candidate = quotedMatch[1].trim();
      return candidate.toLowerCase() === currentExperimentName.toLowerCase() ? undefined : candidate;
    }

    const plainMatch = message.match(/compare(?:\s+this\s+experiment)?\s+(?:against|to|with)\s+([A-Za-z0-9 _-]+)/i);
    if (!plainMatch?.[1]) {
      return undefined;
    }

    const candidate = plainMatch[1].trim().replace(/[.?!,]+$/, '');
    return candidate.toLowerCase() === currentExperimentName.toLowerCase() ? undefined : candidate;
  }

  private truncateSeriesSummaries(baseContext: Omit<GraphAgentContext, 'availableSeries' | 'truncated'>, seriesSummaries: GraphAgentSeriesSummary[]) {
    const result: GraphAgentSeriesSummary[] = [];
    for (const summary of seriesSummaries) {
      const next = [...result, summary];
      const byteLength = JSON.stringify({
        ...baseContext,
        availableSeries: next
      }).length;
      if (byteLength > GraphAgentContextService.MAX_CONTEXT_BYTES) {
        break;
      }
      result.push(summary);
    }

    return result;
  }

  private toSeriesSummary(series: Awaited<ReturnType<GraphAgentContextService['getSelectorVisibleSeriesForThingie']>>[number], primaryThingie: IThingie): GraphAgentSeriesSummary {
    const replicateName = ThingieHelper.getReplicateFullname(primaryThingie);
    if (isAnnotationSeries(series)) {
      return {
        seriesKey: this.getSeriesKey(series),
        thingieId: series.identification.thingieId,
        name: series.identification.systemAnnotation ? 'System annotations' : 'User annotations',
        replicateName,
        path: `Replicate / ${ThingieHelper.getReplicateFullname(primaryThingie)}`,
        valueType: series.valueConfig.type,
        processIndex: -1,
        taskIndex: -1,
        output: series.identification.systemAnnotation ? 'systemAnnotation' : 'userAnnotation'
      };
    }

    if (isDeviceSeries(series)) {
      const propertyName = series.dataItemDescription[series.identification.property]?.valueInfo?.[0]?.name
        ?? series.identification.property;
      return {
        seriesKey: this.getSeriesKey(series),
        thingieId: series.identification.thingieId,
        name: propertyName,
        replicateName,
        path: `Device / ${series.identification.deviceAddress}`,
        valueType: series.valueConfig.type,
        processIndex: -1,
        taskIndex: -1,
        output: `${series.identification.packetType}:${series.identification.property}`
      };
    }

    if (!isThingieSeries(series)) {
      throw new Error('Unsupported series type for graph agent context.');
    }

    const process = primaryThingie.processes[series.identification.processIndex];
    const task = process?.tasks[series.identification.taskIndex];
    const outputName = series.identification.dimensionName || task?.outputs?.[series.identification.output]?.name || series.identification.output;
    return {
      seriesKey: this.getSeriesKey(series),
      thingieId: series.identification.thingieId,
      name: outputName,
      replicateName,
      path: `${process?.name ?? 'Process'} / ${task?.name ?? 'Task'}`,
      taskLabel: task?.name ?? undefined,
      valueType: series.valueConfig.type,
      processIndex: series.identification.processIndex,
      taskIndex: series.identification.taskIndex,
      output: series.identification.output,
      sampleBundleKey: isScatterSeries(series)
        ? buildExperimentSampleBundleKey(primaryThingie._id ?? '')
        : undefined
    };
  }

  private getSeriesKey(series: Awaited<ReturnType<GraphAgentContextService['getSelectorVisibleSeriesForThingie']>>[number]): string {
    switch (series.identification.type) {
      case 'thingieSeries':
        return [
          'thingie',
          series.identification.thingieId ?? '',
          series.identification.processIndex,
          series.identification.taskIndex,
          series.identification.output,
          series.identification.offlineData ? 'offline' : 'live'
        ].join(':');
      case 'deviceSeries':
        return [
          'device',
          series.identification.thingieId ?? '',
          series.identification.deviceAddress,
          series.identification.packetType,
          series.identification.property
        ].join(':');
      case 'annotationSeries':
        return [
          'annotation',
          series.identification.thingieId ?? '',
          series.identification.systemAnnotation ? 'system' : 'user'
        ].join(':');
    }
  }

  private getPrimaryThingieId(graphConfig: IGraphConfig): string | undefined {
    if (graphConfig.thingie) {
      return graphConfig.thingie;
    }

    return graphConfig.internalSeriesList
      .map(series => series.identification.thingieId)
      .find((thingieId): thingieId is string => !!thingieId);
  }

  private getTaskDeviceType(task: IThingie['processes'][number]['tasks'][number]): string | undefined {
    const deviceInput = Object.values(task.inputs)
      .find(input => (input as DeviceInputLike).source === 'device') as DeviceInputLike | undefined;
    return deviceInput?.deviceAddress;
  }

  private getAssignedDevices(thingie: IThingie): string[] {
    const deviceAddresses = new Set<string>();
    for (const process of thingie.processes) {
      for (const task of process.tasks) {
        for (const input of Object.values(task.inputs) as DeviceInputLike[]) {
          if (input.source === 'device' && input.deviceAddress) {
            deviceAddresses.add(input.deviceAddress);
          }
        }
      }
    }

    return [...deviceAddresses];
  }

  private async getDeviceSeriesForThingie(thingie: IThingie) {
    const assignedDevices = this.getAssignedDevices(thingie);
    if (assignedDevices.length === 0) {
      return [];
    }

    const deviceSeries = await Promise.all(assignedDevices.map(async deviceAddress => {
      try {
        const seriesList = await this.deviceService.getDeviceDataSeries(deviceAddress);
        return seriesList.map(series => ({
          ...series,
          identification: {
            ...series.identification,
            thingieId: thingie._id
          }
        }));
      } catch {
        return [];
      }
    }));

    return deviceSeries.flat();
  }
}
